import React, { useState, useEffect } from 'react';

type Action = AddAction | DeleteAction | ChangeAction | SetAction;
type Actions = Action[];

type SetAction = {
  type: 'set';
  newState: React.JSX.Element[];
};

type AddAction = {
  type: 'add';
  newElement: React.JSX.Element;
};

type DeleteAction = {
  type: 'delete';
  deleteElement: React.JSX.Element;
};

type ChangeAction = {
  type: 'change';
  newData: Props;
  prevProps: Props;
  elementId: string;
};

type Props = {
  position: THREE.Vector3;
  scale: THREE.Vector3;
  rotation: THREE.Vector3;
};

const MAX_ACTIONS_RECORD = 10;

const updateState = (
  action: Action,
  setState: React.Dispatch<React.SetStateAction<React.JSX.Element[]>>
) => {
  switch (action.type) {
    case 'add':
      setState((prev) => [action.newElement, ...prev]);
      break;
    case 'set':
      setState(() => action.newState);
      break;
    case 'delete':
      setState((current) =>
        current.filter((asset) => {
          const selectedThreeObj = action.deleteElement.props.asset.ref.current;
          const currentThreeObj = asset.props.asset.ref.current;
          return currentThreeObj?.uuid !== selectedThreeObj?.uuid;
        })
      );
      break;
    case 'change':
      // Handle change action if needed
      break;
  }
};

function recordHistory(
  action: Action,
  setActions: React.Dispatch<React.SetStateAction<Actions>>
) {
  if (action.type === 'set') {
    return;
  }
  setActions((prev) => {
    const newActions = [action, ...prev];
    if (newActions.length > MAX_ACTIONS_RECORD) {
      newActions.pop();
    }
    return newActions;
  });
}

function revertAction(
  setHistoryState: React.Dispatch<React.SetStateAction<Actions>>,
  setOppositeHistoryState: React.Dispatch<React.SetStateAction<Actions>>,
  setState: React.Dispatch<React.SetStateAction<React.JSX.Element[]>>,
  checkIsAssetToDelete
) {
  let lastAction: Action;

  setHistoryState((prev) => {
    lastAction = prev.shift()!;
    return [...prev];
  });

  if (!lastAction!) {
    return;
  }

  if (lastAction.type === 'add') {
    const action = {
      type: 'delete',
      deleteElement: (lastAction as AddAction).newElement,
    } as Action;
    updateState(action, setState);
    recordHistory(action, setOppositeHistoryState);
  } else if (lastAction.type === 'delete') {
    const action = {
      type: 'add',
      newElement: (lastAction as DeleteAction).deleteElement,
    } as Action;
    checkIsAssetToDelete(
      (lastAction as DeleteAction).deleteElement?.props?.asset?.id
    );
    updateState(action, setState);
    recordHistory(action, setOppositeHistoryState);
  } else if (lastAction.type === 'change') {
    const act = lastAction as ChangeAction;
    let missElement = false;
    setState((prev) => {
      const selected = prev.find(
        (elem) => elem.props.asset.ref.current?.uuid === act.elementId
      );
      if (!selected) {
        missElement = true;
        return prev;
      }
      const threeObj = selected?.props.asset.ref.current;
      threeObj.position.set(
        act.prevProps.position.x,
        act.prevProps.position.y,
        act.prevProps.position.z
      );
      threeObj.scale.set(
        act.prevProps.scale.x,
        act.prevProps.scale.y,
        act.prevProps.scale.z
      );
      threeObj.rotation.set(
        act.prevProps.rotation.x,
        act.prevProps.rotation.y,
        act.prevProps.rotation.z
      );

      return prev;
    });
    if (missElement) {
      return;
    }
    recordHistory(
      {
        type: 'change',
        newData: { ...act.prevProps },
        prevProps: { ...act.newData },
        elementId: act.elementId,
      },
      setOppositeHistoryState
    );
  }
}

export default function useUndoRedo({ checkIsAssetToDelete }) {
  const [state, setState] = useState<React.JSX.Element[]>([]);
  const [actions, setActions] = useState<Actions>([]);
  const [redoActions, setRedoActions] = useState<Actions>([]);

  function dispatch(action: Action) {
    updateState(action, setState);
    recordHistory(action, setActions);

    if (
      action.type === 'add' ||
      action.type === 'delete' ||
      action.type === 'change'
    ) {
      window.dispatchEvent(
        new CustomEvent('scene_asset_changed', {
          detail: { hasChanges: true },
        })
      );
    }
  }

  function undo() {
    revertAction(setActions, setRedoActions, setState, checkIsAssetToDelete);

    window.dispatchEvent(
      new CustomEvent('scene_asset_changed', {
        detail: { hasChanges: true },
      })
    );
  }

  function redo() {
    revertAction(setRedoActions, setActions, setState, checkIsAssetToDelete);

    window.dispatchEvent(
      new CustomEvent('scene_asset_changed', {
        detail: { hasChanges: true },
      })
    );
  }

  return {
    state,
    dispatch,
    history: actions,
    redoHistory: redoActions,
    undo,
    redo,
  };
}
